import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import React from 'react';
import type { ApiKey } from '../../types';
import { useTranslation } from 'react-i18next';
import { CapabilityBadge } from './ScopeBadges';
import { displayName } from './utils';

export interface RevokeConfirmationDialogProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onConfirm: () => void;
}

export const RevokeConfirmationDialog: React.FC<RevokeConfirmationDialogProps> = ({
  open,
  apiKey,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('apiKeys.revoke.title')}</DialogTitle>
      <DialogContent>
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
          <p className="text-sm text-red-800 dark:text-red-200">
            {t('apiKeys.revoke.warningBefore')} <strong>{t('apiKeys.revoke.irreversible')}</strong>.{' '}
            {t('apiKeys.revoke.warningAfter')}
          </p>
        </div>

        <p className="text-gray-700 dark:text-slate-300">
          {t('apiKeys.revoke.confirm', { name: apiKey ? displayName(apiKey) : '' })}
        </p>

        {/* Show capabilities being lost */}
        {apiKey && apiKey.capabilities.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 text-xs text-gray-500 dark:text-slate-400">
              {t('apiKeys.revoke.accessTo')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {apiKey.capabilities.map((cap) => (
                <CapabilityBadge key={cap.capabilityCode} scope={cap} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 rounded bg-gray-100 p-3 dark:bg-slate-900">
          <code className="font-mono text-sm text-gray-800 dark:text-slate-300">
            {apiKey?.publicId}
          </code>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('apiKeys.revoke.cancel')}</Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          {t('apiKeys.revoke.revokeKey')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface RotateConfirmationDialogProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onConfirm: () => void;
}

export const RotateConfirmationDialog: React.FC<RotateConfirmationDialogProps> = ({
  open,
  apiKey,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('apiKeys.rotate.title')}</DialogTitle>
      <DialogContent>
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {t('apiKeys.rotate.warning')}
          </p>
        </div>
        <p className="text-gray-700 dark:text-slate-300">
          {t('apiKeys.rotate.confirm', { name: apiKey ? displayName(apiKey) : '' })}
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('apiKeys.rotate.cancel')}</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          startIcon={<RefreshCw size={16} />}
          disabled={!apiKey}
        >
          {t('apiKeys.rotate.rotateKey')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
